import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { apolloClient } from '@/plugins/graphql';
import gql from 'graphql-tag';

/**
 * One firing-alert row as returned by the alertLog query. Mirrors the
 * backend AlertLog entity shape 1:1 — the store surfaces this to
 * components without re-mapping.
 */
export interface AlertLogEntry {
    id: number;
    operationId: string;
    alertName: string;
    triggerKind: string;
    subjectAssetId: string | null;
    subjectAssetName: string | null;
    cpaMeters: number | null;
    tcpaSeconds: number | null;
    severity: string | null;
    description: string | null;
    firedAtUtc: string;
}

const ALERT_LOG_QUERY = gql`
    query AlertLog($limit: Int!) {
        alertLog(limit: $limit) {
            id
            operationId
            alertName
            triggerKind
            subjectAssetId
            subjectAssetName
            cpaMeters
            tcpaSeconds
            severity
            description
            firedAtUtc
        }
    }
`;

const CONFIGURE_ALERTS_MUTATION = gql`
    mutation ConfigureAlerts($input: AlertConfigurationInput!) {
        configureAlerts(input: $input)
    }
`;

/**
 * Definition input matching the backend's AlertDefinitionInput shape.
 * Kept explicit so callers materialising alerts from a parsed .sch AST
 * (or manually via the assistant chatbox) construct the exact shape the
 * backend mutation expects.
 */
export interface AlertDefinitionInput {
    name: string;
    triggerKind: string;
    subjectAssetName: string;
    areaName?: string | null;
    referencePointName?: string | null;
    cpaMeters?: number | null;
    tcpaSeconds?: number | null;
    severity?: string | null;
    description?: string | null;
    schedule?: {
        dailyFrom?: string | null;
        dailyTo?: string | null;
        daysOfWeek?: string[] | null;
        activeWindows?: string[] | null;
    } | null;
}

const POLL_INTERVAL_MS = 5_000;
const HISTORY_LIMIT = 50;

/**
 * Pinia store for CPA/TCPA firing alerts. Two responsibilities:
 *
 *   1) Poll the backend alertLog every 5 s so a connected user sees new
 *      firings within a tick of them being persisted. Latest first.
 *   2) Expose a `configureAlerts` action that pushes a parsed alert set
 *      to the backend evaluator (called from the plan editor on save).
 *
 * v1 uses polling instead of a GraphQL subscription so we don't take a
 * dependency on graphql-ws — the backend already exposes the subscription
 * endpoint for a future switch.
 */
export const useAlertsStore = defineStore('alerts', () => {
    const recentAlerts = ref<AlertLogEntry[]>([]);
    // Row id of the newest alert we've displayed a toast for. Used to
    // decide which arrivals count as "new" between polls without adding
    // the whole ring buffer to component state.
    const highestSeenId = ref<number>(0);
    // Ids for which the "unacknowledged" toast is still showing. The panel
    // clears these when the user opens it; new arrivals top up the set.
    const unacknowledgedIds = ref<Set<number>>(new Set());

    let pollTimer: ReturnType<typeof setInterval> | null = null;

    const unacknowledgedCount = computed(() => unacknowledgedIds.value.size);
    const newestAlert = computed<AlertLogEntry | null>(
        () => recentAlerts.value.length > 0 ? recentAlerts.value[0] : null);

    async function refresh() {
        try {
            const result = await apolloClient.query({
                query: ALERT_LOG_QUERY,
                variables: { limit: HISTORY_LIMIT },
                fetchPolicy: 'no-cache',
            });
            const rows: AlertLogEntry[] = result.data?.alertLog ?? [];
            const newIds = rows
                .filter(r => r.id > highestSeenId.value)
                .map(r => r.id);
            if (newIds.length > 0) {
                for (const id of newIds) unacknowledgedIds.value.add(id);
                // Trigger reactive update — Set mutation isn't tracked.
                unacknowledgedIds.value = new Set(unacknowledgedIds.value);
                highestSeenId.value = Math.max(highestSeenId.value, ...newIds);
            }
            recentAlerts.value = rows;
        } catch (err) {
            console.error('[AlertsStore] failed to fetch alertLog:', err);
        }
    }

    function startPolling() {
        if (pollTimer !== null) return;
        // Initial fetch primes highestSeenId with the current head so a
        // user opening the app doesn't get toast-spammed by every
        // pre-existing row.
        void refresh().then(() => {
            highestSeenId.value = recentAlerts.value.length > 0
                ? recentAlerts.value[0].id
                : 0;
            unacknowledgedIds.value = new Set();
        });
        pollTimer = setInterval(refresh, POLL_INTERVAL_MS);
    }

    function stopPolling() {
        if (pollTimer !== null) {
            clearInterval(pollTimer);
            pollTimer = null;
        }
    }

    function acknowledgeAll() {
        unacknowledgedIds.value = new Set();
    }

    async function configureAlerts(alerts: AlertDefinitionInput[]) {
        try {
            const result = await apolloClient.mutate({
                mutation: CONFIGURE_ALERTS_MUTATION,
                variables: { input: { alerts } },
            });
            return result.data?.configureAlerts ?? 0;
        } catch (err) {
            console.error('[AlertsStore] configureAlerts failed:', err);
            return 0;
        }
    }

    return {
        recentAlerts,
        unacknowledgedCount,
        newestAlert,
        startPolling,
        stopPolling,
        acknowledgeAll,
        configureAlerts,
        refresh,
    };
});
